import { t, isValidEmail } from '@viral-space/shared';
import {
  COLORS, FONT, GLOW, el, css,
  createScreenRoot, buildStarfield, createButton, createInput, createTitle,
  injectSharedKeyframes,
} from './ui-shared';
import { createLanguageSelector } from './language-selector';
import { maybeShowInstallPrompt, type InstallPrompt } from './install-prompt';
import { isElectronDesktop } from '../input/mobile';

export interface AuthResult {
  userId: string;
  username: string;
  token: string;
  isNewPlayer: boolean;
  /**
   * `steam` is emitted by the desktop "Continue with Steam" button — a
   * credential-less identity login handled entirely from the verified
   * Steam ticket (no username/password).
   */
  mode: 'login' | 'signup' | 'steam';
  password?: string;
  email?: string;
}

type AuthMode = 'login' | 'signup' | 'forgot';

/**
 * Async bridges to the two password-reset round-trips. Injected by the
 * bootstrap (which owns the NetworkClient + resolved server URL) so the
 * auth screen stays a pure UI component.
 */
export interface PasswordResetHandlers {
  request(email: string): Promise<{ success: boolean; error?: string }>;
  confirm(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<{ success: boolean; error?: string }>;
}

export class AuthScreen {
  private container: HTMLElement;
  private root: HTMLElement;
  private onAuth: (result: AuthResult) => void;
  // Optional server URL surfaced as a small footer for on-device
  // debugging. Populated by the game bootstrap so that testers (and us)
  // can see exactly which endpoint a packaged native build is pointed at
  // without having to attach Safari / Chrome remote inspector.
  private serverUrl: string | undefined;

  private mode: AuthMode = 'login';
  private formContent!: HTMLElement;
  private usernameInput!: HTMLInputElement;
  private passwordInput!: HTMLInputElement;
  private emailInput!: HTMLInputElement;
  private codeInput!: HTMLInputElement;
  private newPasswordInput!: HTMLInputElement;
  private errorEl!: HTMLElement;
  private statusEl!: HTMLElement;
  private submitBtn!: HTMLButtonElement;
  /** Which step of the forgot-password flow is on screen. */
  private forgotStage: 'email' | 'code' = 'email';
  /** Email the reset code was requested for (carried across rebuilds). */
  private forgotEmail = '';
  private resetHandlers?: PasswordResetHandlers;
  /** Desktop-only "Continue with Steam" button (undefined on web/mobile). */
  private steamBtn?: HTMLButtonElement;
  /**
   * Username carried over between rebuilds (mode tab toggle, locale
   * switch) and pre-fills set by the auto-login fallback. Stored on the
   * instance so that {@link buildContent} can re-apply it whenever the
   * DOM is regenerated.
   */
  private prefilledUsername = '';
  /**
   * Live "Add to Home Screen" modal, if currently shown. Held so a
   * locale switch can rebuild it with translated copy (the modal lives
   * outside `this.root` since it overlays the entire viewport).
   */
  private installPrompt: InstallPrompt | null = null;
  /**
   * Whether we've already attempted to surface the install prompt this
   * session. Combined with the modal's own localStorage gate so even
   * if the player is shown the auth screen twice in one page lifetime
   * (sign-out + re-show) we don't try to construct a fresh modal —
   * `maybeShowInstallPrompt` would skip it anyway, but checking here
   * avoids the wasted DOM read.
   */
  private installPromptShownThisSession = false;

  constructor(
    container: HTMLElement,
    onAuth: (result: AuthResult) => void,
    serverUrl?: string,
    resetHandlers?: PasswordResetHandlers,
  ) {
    this.container = container;
    this.onAuth = onAuth;
    this.serverUrl = serverUrl;
    this.resetHandlers = resetHandlers;
    injectSharedKeyframes();
    this.root = createScreenRoot(container);
    buildStarfield(this.root);
    this.buildContent();
  }

  private rebuild(): void {
    this.root.remove();
    this.root = createScreenRoot(this.container);
    buildStarfield(this.root);
    this.buildContent();
    this.root.style.display = 'flex';
  }

  private buildContent(): void {
    const content = el('div', {
      position: 'relative',
      zIndex: '1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
      padding: '24px',
      animation: 'vs-fade-in 0.5s ease-out',
    });
    content.classList.add('vs-screen-content');
    this.root.appendChild(content);

    content.appendChild(createTitle(t('game.title')));

    const subtitle = el('div', {
      fontSize: 'clamp(10px, 1.5vw, 14px)',
      color: COLORS.dim,
      marginTop: '-12px',
      letterSpacing: '4px',
    }, t('game.subtitle'));
    subtitle.classList.add('vs-screen-subtitle');
    content.appendChild(subtitle);

    this.buildLanguageSelector(content);

    const panel = el('div', {
      background: 'rgba(0,12,0,0.85)',
      border: `1px solid ${COLORS.dim}`,
      padding: 'clamp(20px, 4vw, 40px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      maxWidth: '420px',
      width: '90vw',
    });
    panel.classList.add('vs-screen-panel');
    content.appendChild(panel);

    const tabs = el('div', {
      display: 'flex',
      gap: '0',
      width: '100%',
    });

    const loginTab = this.createTab(
      t('auth.login'),
      this.mode === 'login',
      () => { this.mode = 'login'; this.forgotStage = 'email'; this.rebuild(); },
    );
    const signupTab = this.createTab(
      t('auth.signup'),
      this.mode === 'signup',
      () => { this.mode = 'signup'; this.forgotStage = 'email'; this.rebuild(); },
    );
    tabs.appendChild(loginTab);
    tabs.appendChild(signupTab);
    panel.appendChild(tabs);

    this.formContent = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
    });
    this.formContent.classList.add('vs-screen-form');
    panel.appendChild(this.formContent);

    if (this.mode === 'forgot') {
      this.buildForgotForm();
    } else {
      this.buildCredentialsForm();
    }

    // On-device diagnostic: show the resolved server URL under the form
    // so we can verify the packaged native build is pointed at the
    // correct endpoint without attaching a remote inspector. Only shown
    // when the bootstrap passes one in; hidden in the common case.
    if (this.serverUrl && this.serverUrl.match(/localhost/)) {
      const serverRow = el('div', {
        fontSize: '9px',
        color: COLORS.dim,
        letterSpacing: '1px',
        textTransform: 'none',
        marginTop: '8px',
        textAlign: 'center',
        maxWidth: '90vw',
        wordBreak: 'break-all',
        opacity: '0.7',
      }, `server: ${this.serverUrl}`);
      this.formContent.appendChild(serverRow);
    }

    // Desktop / Steam shell only: a quit button so the player can close
    // the game from the login screen without reaching for Alt+F4 / Cmd+Q.
    // The web + mobile builds have no window to close, so it's hidden there.
    if (isElectronDesktop()) {
      const exitBtn = createButton(
        t('menu.exitGame'),
        () => { window.zerionDesktop?.quit?.(); },
        'ghost',
      );
      css(exitBtn, { marginTop: '4px', opacity: '0.5', fontSize: '10px' });
      content.appendChild(exitBtn);
    }
  }

  private buildFieldLabel(text: string): HTMLElement {
    return el('label', {
      fontSize: '10px',
      color: COLORS.dim,
      alignSelf: 'flex-start',
      paddingLeft: '4px',
    }, text);
  }

  /** Status (info) + error rows shared by every form variant. */
  private buildFeedbackRows(): void {
    this.statusEl = el('div', {
      fontSize: '11px',
      color: COLORS.primary,
      minHeight: '0',
      textAlign: 'center',
      textTransform: 'none',
      maxWidth: '90vw',
      wordBreak: 'break-word',
    });
    this.formContent.appendChild(this.statusEl);

    this.errorEl = el('div', {
      fontSize: '11px',
      color: COLORS.danger,
      minHeight: '16px',
      textAlign: 'center',
      textTransform: 'none',
      maxWidth: '90vw',
      wordBreak: 'break-word',
    });
    this.formContent.appendChild(this.errorEl);
  }

  private buildCredentialsForm(): void {
    if (this.mode === 'signup') {
      this.formContent.appendChild(this.buildFieldLabel(t('auth.email')));
      this.emailInput = createInput('pilot@sector.net', 'email');
      this.formContent.appendChild(this.emailInput);
    }

    this.formContent.appendChild(this.buildFieldLabel(t('auth.username')));
    this.usernameInput = createInput('PILOT-001', 'text', 16);
    if (this.prefilledUsername) {
      this.usernameInput.value = this.prefilledUsername;
    }
    this.formContent.appendChild(this.usernameInput);

    this.formContent.appendChild(this.buildFieldLabel(t('auth.password')));
    this.passwordInput = createInput('••••••••', 'password', 64);
    this.formContent.appendChild(this.passwordInput);

    this.buildFeedbackRows();

    const buttonLabel = this.mode === 'signup'
      ? t('auth.createAccount')
      : t('auth.enter');
    this.submitBtn = createButton(buttonLabel, () => this.handleSubmit());
    this.formContent.appendChild(this.submitBtn);

    // Account recovery entry point. Only rendered when the bootstrap
    // wired up the reset round-trips (it always does in production).
    if (this.mode === 'login' && this.resetHandlers) {
      const forgot = el('div', {
        fontSize: '10px',
        color: COLORS.dim,
        textDecoration: 'underline',
        cursor: 'pointer',
        pointerEvents: 'auto',
        textTransform: 'none',
        marginTop: '2px',
      }, t('auth.forgotPassword'));
      forgot.addEventListener('click', () => {
        this.mode = 'forgot';
        this.forgotStage = 'email';
        this.rebuild();
      });
      this.formContent.appendChild(forgot);
    }

    // Desktop / Steam shell only: a one-click "Continue with Steam" that
    // signs the player in from their Steam identity — no username, email
    // or password required. The web + mobile builds have no Steam bridge,
    // so it's hidden there. On failure (Steam not running) the handler in
    // the bootstrap surfaces an error and the password form stays usable.
    if (isElectronDesktop() && window.zerionDesktop?.steam) {
      const divider = el('div', {
        fontSize: '9px',
        color: COLORS.dim,
        letterSpacing: '2px',
        opacity: '0.6',
        margin: '2px 0',
      }, t('auth.or'));
      this.formContent.appendChild(divider);

      this.steamBtn = createButton(
        t('auth.continueWithSteam'),
        () => this.handleSteamLogin(),
      );
      this.formContent.appendChild(this.steamBtn);
    }

    this.passwordInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.handleSubmit();
    });
  }

  /**
   * Forgot-password flow. Stage 'email' asks for the account email and
   * requests a code; stage 'code' collects the emailed code + the new
   * password and redeems it.
   */
  private buildForgotForm(): void {
    const title = el('div', {
      fontSize: '12px',
      color: COLORS.primary,
      letterSpacing: '2px',
      marginTop: '4px',
    }, t('auth.resetPasswordTitle'));
    this.formContent.appendChild(title);

    const instructions = el('div', {
      fontSize: '11px',
      color: COLORS.dim,
      textAlign: 'center',
      textTransform: 'none',
      lineHeight: '1.5',
      maxWidth: '320px',
    }, this.forgotStage === 'email'
      ? t('auth.resetInstructions')
      : t('auth.resetEmailSent'));
    this.formContent.appendChild(instructions);

    if (this.forgotStage === 'email') {
      this.formContent.appendChild(this.buildFieldLabel(t('auth.email')));
      this.emailInput = createInput('pilot@sector.net', 'email');
      if (this.forgotEmail) this.emailInput.value = this.forgotEmail;
      this.formContent.appendChild(this.emailInput);

      this.buildFeedbackRows();

      this.submitBtn = createButton(
        t('auth.sendResetCode'),
        () => { void this.handleForgotSubmit(); },
      );
      this.formContent.appendChild(this.submitBtn);

      this.emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') void this.handleForgotSubmit();
      });
    } else {
      this.formContent.appendChild(this.buildFieldLabel(t('auth.resetCode')));
      this.codeInput = createInput('000000', 'text', 6);
      this.formContent.appendChild(this.codeInput);

      this.formContent.appendChild(this.buildFieldLabel(t('auth.newPassword')));
      this.newPasswordInput = createInput('••••••••', 'password', 64);
      this.formContent.appendChild(this.newPasswordInput);

      this.buildFeedbackRows();

      this.submitBtn = createButton(
        t('auth.resetPassword'),
        () => { void this.handleForgotSubmit(); },
      );
      this.formContent.appendChild(this.submitBtn);

      this.newPasswordInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') void this.handleForgotSubmit();
      });
    }

    const back = el('div', {
      fontSize: '10px',
      color: COLORS.dim,
      textDecoration: 'underline',
      cursor: 'pointer',
      pointerEvents: 'auto',
      textTransform: 'none',
      marginTop: '2px',
    }, t('auth.backToLogin'));
    back.addEventListener('click', () => {
      this.mode = 'login';
      this.forgotStage = 'email';
      this.rebuild();
    });
    this.formContent.appendChild(back);
  }

  private async handleForgotSubmit(): Promise<void> {
    if (!this.resetHandlers) return;

    if (this.forgotStage === 'email') {
      const email = this.emailInput.value.trim();
      if (!email) {
        this.showError(t('auth.emailRequired'));
        return;
      }
      if (!isValidEmail(email)) {
        this.showError(t('auth.emailInvalid'));
        return;
      }
      this.forgotEmail = email;
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = '...';
      try {
        const resp = await this.resetHandlers.request(email);
        if (!resp.success) {
          this.showError(resp.error ?? t('auth.emailInvalid'));
          this.resetForm();
          return;
        }
        this.forgotStage = 'code';
        this.rebuild();
      } catch (err) {
        this.showError(err instanceof Error ? err.message : 'Connection failed');
        this.resetForm();
      }
      return;
    }

    const code = this.codeInput.value.trim();
    const newPassword = this.newPasswordInput.value;
    if (code.length === 0) {
      this.showError(t('auth.resetCodeRequired'));
      return;
    }
    if (newPassword.length < 4) {
      this.showError(t('auth.passwordTooShort'));
      return;
    }
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = '...';
    try {
      const resp = await this.resetHandlers.confirm(this.forgotEmail, code, newPassword);
      if (!resp.success) {
        this.showError(resp.error ?? t('auth.resetCodeRequired'));
        this.resetForm();
        return;
      }
      this.mode = 'login';
      this.forgotStage = 'email';
      this.rebuild();
      this.showStatus(t('auth.resetSuccess'));
    } catch (err) {
      this.showError(err instanceof Error ? err.message : 'Connection failed');
      this.resetForm();
    }
  }

  private createTab(text: string, active: boolean, onClick: () => void): HTMLElement {
    const tab = el('div', {
      flex: '1',
      textAlign: 'center',
      padding: '10px',
      fontSize: '12px',
      letterSpacing: '2px',
      cursor: 'pointer',
      color: active ? '#000' : COLORS.primary,
      background: active ? COLORS.primary : 'transparent',
      borderBottom: active ? 'none' : `1px solid ${COLORS.dim}`,
      transition: 'all 0.2s',
      pointerEvents: 'auto',
    }, text);
    tab.addEventListener('click', onClick);
    return tab;
  }

  private buildLanguageSelector(parent: HTMLElement): void {
    const selector = createLanguageSelector({
      labelText: t('menu.language'),
      onChange: () => this.rebuild(),
    });
    parent.appendChild(selector);
  }

  showError(msg: string): void {
    this.errorEl.textContent = msg;
    this.errorEl.style.animation = 'vs-flicker 0.3s';
    setTimeout(() => { this.errorEl.style.animation = ''; }, 300);
  }

  /** Positive feedback line (e.g. "password updated"). */
  showStatus(msg: string): void {
    if (this.statusEl) this.statusEl.textContent = msg;
  }

  /**
   * Pre-fill the username field. Called from the auto-login fallback so
   * the player only has to retype their password (not their callsign)
   * when their saved credentials no longer authenticate.
   */
  prefillUsername(name: string): void {
    this.prefilledUsername = name;
    if (this.usernameInput) {
      this.usernameInput.value = name;
    }
  }

  private handleSubmit(): void {
    const mode = this.mode;
    if (mode === 'forgot') return;
    const username = this.usernameInput.value.trim();
    const password = this.passwordInput.value;
    const email = mode === 'signup' ? this.emailInput?.value.trim() : undefined;

    if (!username) {
      this.showError(t('auth.usernameRequired'));
      return;
    }
    if (password.length < 4) {
      this.showError(t('auth.passwordTooShort'));
      return;
    }
    if (mode === 'signup') {
      // Email is required on new signups — it powers the welcome email
      // and the forgot-password flow, and the server rejects malformed
      // addresses anyway, so catch both problems before the round-trip.
      if (!email) {
        this.showError(t('auth.emailRequired'));
        return;
      }
      if (!isValidEmail(email)) {
        this.showError(t('auth.emailInvalid'));
        return;
      }
    }

    this.submitBtn.disabled = true;
    this.submitBtn.textContent = '...';

    this.onAuth({
      userId: '',
      username,
      token: '',
      isNewPlayer: mode === 'signup',
      mode,
      password,
      email,
    });
  }

  private handleSteamLogin(): void {
    if (!this.steamBtn) return;
    this.steamBtn.disabled = true;
    this.steamBtn.textContent = '...';
    this.onAuth({
      userId: '',
      username: '',
      token: '',
      isNewPlayer: false,
      mode: 'steam',
    });
  }

  resetForm(): void {
    if (this.submitBtn) {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent =
        this.mode === 'forgot'
          ? (this.forgotStage === 'email'
              ? t('auth.sendResetCode')
              : t('auth.resetPassword'))
          : this.mode === 'signup'
            ? t('auth.createAccount')
            : t('auth.enter');
    }
    if (this.steamBtn) {
      this.steamBtn.disabled = false;
      this.steamBtn.textContent = t('auth.continueWithSteam');
    }
  }

  show(): void {
    // Re-enable the submit button and restore its label so that
    // returning to the auth screen after a sign-out (or a failed
    // submit that bailed out before the network callback) doesn't
    // leave the "..." placeholder text stuck on a disabled button.
    this.resetForm();
    this.root.style.display = 'flex';
    setTimeout(() => this.usernameInput?.focus(), 100);
    this.maybeShowInstallPrompt();
  }

  hide(): void {
    this.root.style.display = 'none';
    if (this.installPrompt) {
      this.installPrompt.dispose();
      this.installPrompt = null;
    }
  }

  /**
   * On the very first auth screen impression, surface a modal that
   * tells mobile-web users how to add Zerion to their Home Screen for
   * a better experience. Skipped silently inside Capacitor, on
   * desktop, when already running standalone, and after the player
   * has dismissed it once. Idempotent across repeat `show()` calls.
   */
  private maybeShowInstallPrompt(): void {
    if (this.installPromptShownThisSession) return;
    if (this.installPrompt) return;
    this.installPromptShownThisSession = true;
    this.installPrompt = maybeShowInstallPrompt(this.container);
  }
}
